import { Query } from '@/_core/cqrs/types/queries';

export class FeedbackPortalInitQuery extends Query<FeedbackPortalInitQueryResult> {
    constructor(public ProductSlug: string, public FeedbackId: string = null) {
        super('FeedbackPortalInitQuery');
    }
}

export class ArchivedFeedbacksQuery extends Query<ArchivedFeedbacksQueryResult> {
    constructor(public FeedbackPortalId: string) {
        super('ArchivedFeedbacksQuery');
    }
}

export class IdeaDetailsQuery extends Query<IdeaDetailsQueryResult> {
    constructor(public FeedbackPortalId: string, public IdeaId: string) {
        super('IdeaDetailsQuery');
    }
}

export class FeedbackAttachmentsQuery extends Query<FeedbackAttachmentsQueryResult> {
    constructor(public FeedbackPortalId: string, public FeedbackId: string) {
        super('FeedbackAttachmentsQuery');
    }
}

export class FeedbackCommentsQuery extends Query<FeedbackCommmentsQueryResult> {
    constructor(public FeedbackPortalId: string, public FeedbackId: string) {
        super('FeedbackCommentsQuery');
    }
}

export class MyStorymapsQuery extends Query<MyStorymapsQueryResult> {
    constructor(public FeedbackPortalId: string) {
        super('MyStorymapsQuery');
    }
}

export class GetStorymapCards extends Query<StorymapCard[]> {
    /**
     * @param StorymapId The ID of the storymap.
     * @param LinkedRoadmapCardId If provided, indicates that subtasks linked to the roadmap card will be included even from archived releases
     */
    constructor(public StorymapId: string, public LinkedRoadmapCardId: string = null) {
        super('GetStorymapCards');
    }
}

export interface StorymapCard {
    Id: string;
    Title: string;
    Key: string;
    LinkedItemKey: string;
    Children: StorymapCard[];
}

export interface MyStorymapsQueryResult {
    Storymaps: SimpleStorymap[];
}

export interface SimpleStorymap {
    Id: string;
    Title: string;
    Description: string;
}

export interface FeedbackAttachmentsQueryResult {
    Attachments: AttachmentModel[];
}

export interface AttachmentModel {
    Id: string;
    Name: string;
    HasThumbnail: boolean;
    Extension: string;
    UploadedBy: string;
    FileSize: number;
}

export interface FeedbackCommmentsQueryResult {
    Comments: CommentModel[];
}

export interface IdeaDetailsQueryResult {
    Comments: CommentModel[];
    Contacts: IdeaContactModel[];
    Feedbacks: FeedbackModel[];
    Attachments: AttachmentModel[];
    WebLinks: WebLinkModel[];
    Upvotes: IdeaUpvoteModel[];
    Portals: IdeaDetailsPortalModel[];
}

export interface IdeaUpvoteModel {
    Id: string;
    UpvoteType: IdeaUpvoteType;
    MemberId: string;
    Email: string;
    IsEmailAddressConfirmed: boolean;
    CreatedAt: string;
}

export enum IdeaUpvoteType {
    WorkspaceMember,
    ExternalUser,
}

export interface CommentModel {
    Id: string;
    Comment: string;
    CreatedAt: string;
    CreatedBy: string;
    CreatedById: string;
}

export interface IdeaContactModel {
    Id: string;
    Email: string;
    CreatedAt: string;
}

export interface IdeaDetailsPortalModel {
    Name: string;
    Slug: string;
}

export enum IdeaLabelColor {
    NotDefined,

    Blue,
    LightBlue,
    Red,
    Yellow,
    Orange,
    Green,
    Purple,
    Pink,
    Grey,
}

export interface IdeaLabelModel {
    Id: string;
    Name: string;
    IsPublic: boolean;
    Color: IdeaLabelColor;
    Order: number;
}

export interface FeedbackPortalInitQueryResult {
    HasSeenFeedbackPortalOnboarding: boolean;

    FeedbackPortalId: string;
    Feedbacks: FeedbackModel[];
    FeedbackCategories: FeedbackCategoryModel[];
    FeedbackTags: FeedbackTagModel[];
    Ideas: IdeaModel[];
    IdeaStatuses: IdeaStatusModel[];
    IdeaLabels: IdeaLabelModel[];
    IdeaFilterViews: IdeaFilterViewModel[];
    WorkspaceMembers: WorkspaceMemberModel[];
    ImportEmailAddress: string;
    ApiProductId: string;

    ShouldRedirect: boolean;
    RedirectUrl: string;
}

export interface WorkspaceMemberModel {
    Name: string;
    Role: string;
    IsCollaborator: boolean;
    HasStorymapAccess: boolean;
    Email: string;
    IsInInvitedState: boolean;
}

export interface IdeaFilterViewModel {
    Id: string;
    Name: string;
    LabelIds: string[];
    StatusIds: string[];
}

export interface ArchivedFeedbacksQueryResult {
    Feedbacks: FeedbackModel[];
}

export enum CreatorToolType {
    InApp = 0,
    Stob = 1,
    Email = 2,
    Zapier = 3,
    PublicRoadmap = 4,
}

interface FeedbackModel {
    Id: string;
    Content: string;
    ContactEmail: string;
    CreatorToolType: CreatorToolType;
    IsImportant: boolean;
    AttachmentCount: number;
    CommentCount: number;
    IsArchived: boolean;
    TagIds: string[];

    CreatedBy: string;
    CreatedById: string;
}

export interface AutomationFeedbackModel extends FeedbackModel {
    IdeaId: string;
}

interface FeedbackCategoryModel {
    Id: string;
    Name: string;
    Order: number;
    IsImmutable: boolean;
}

export interface FeedbackTagModel {
    Id: string;
    Name: string;
}

export interface IdeaModel {
    Id: string;
    Title: string;
    Content: string;
    PublicContent: string;
    StatusId: string;
    Order: number;
    CreatorToolType: CreatorToolType;
    ExternalToolName: string;
    IsPublic: boolean;
    IsArchived: boolean;
    AttachmentCount: number;
    CommentCount: number;
    UpvoteCount: number;
    FeedbackCount: number;
    LabelIds: string[];
    FeedbackTagIdsForSuggestion: string[];

    CreatedBy: string;
    CreatedById: string;
}

export interface IdeaStatusModel {
    Id: string;
    Name: string;
    IsPublic: boolean;
    Order: number;
}

export class FeedbackWebLinksQuery extends Query<FeedbackWebLinksQueryResult> {
    constructor(public FeedbackPortalId: string, public FeedbackId: string) {
        super('FeedbackWebLinksQuery');
    }
}

interface FeedbackWebLinksQueryResult {
    WebLinks: WebLinkModel[];
}

export interface WebLinkModel {
    Id: string;
    Url: string;
    Name: string;
    FaviconId: string;
    CreatorUserName: string;
}

export class ArchivedIdeasQuery extends Query<ArchivedIdeasQueryResult> {
    constructor(public FeedbackPortalId: string) {
        super('ArchivedIdeasQuery');
    }
}

export interface ArchivedIdeasQueryResult {
    Ideas: IdeaModel[];
}

// Automation

export class FeedbackAutomationsQuery extends Query<FeedbackAutomationsQueryResult> {
    constructor(public FeedbackPortalId: string) {
        super('FeedbackAutomationsQuery');
    }
}

export class FeedbackAutomationLogsQuery extends Query<FeedbackAutomationLogsQueryResult> {
    constructor(public FeedbackPortalId: string) {
        super('FeedbackAutomationLogsQuery');
    }
}

export interface FeedbackAutomationsQueryResult {
    Automations: FeedbackAutomationModel[];
}

export interface FeedbackAutomationLogsQueryResult {
    AutomationLogs: FeedbackAutomationLogModel[];
    Feedbacks: AutomationFeedbackModel[];
}

export interface FeedbackAutomationLogModel {
    AutomationName: string;
    AutomationId: string;
    FeedbackId: string;

    Actions: FeedbackAutomationActionLogModel[];

    CreatedAt: string;
}

export interface FeedbackAutomationActionLogModel {
    Type: number;
    IdeaId: string;
    IdeaTitle: string;

    CategoryId: string;
    CategoryName: string;

    TagNames: string[];
}

export interface FeedbackAutomationModel {
    Id: string;
    Name: string;
    IsActive: boolean;
    ShouldAllConditionsPass: boolean;
    Conditions: FeedbackAutomationConditionModel[];
    Actions: FeedbackAutomationActionModel[];
}

// Automation - Condition

export interface FeedbackAutomationConditionModel {
    Id: string;

    Type: number;

    CreatorType: number;
    CreatorUserId: string;
    CreatorExternalToolName: string;

    SourceEmailComparerType: number;
    SourceEmailText: string;

    ContentComparerType: number;
    ContentText: string;
}

export enum FeedbackAutomationConditionType {
    Creator = 10,
    SourceEmail = 20,
    Content = 30,
}

export enum FeedbackAutomationConditionCreatorType {
    Undefined = 0,

    IsSpecificUser = 10,
    IsAdmin = 20,
    IsMember = 30,
    IsObserver = 40,
    IsZapier = 50,
    IsEmail = 60,
    IsPublicRoadmap = 70,
    IsApi = 80,
}

export enum FeedbackAutomationConditionSourceEmailComparerType {
    Undefined = 0,

    Contains = 10,
    DoesNotContain = 20,
    IsEqualTo = 30,
}

export enum FeedbackAutomationConditionContentComparerType {
    Undefined = 0,

    Contains = 10,
    DoesNotContain = 20,
    IsEqualTo = 30,
}

// Automation - Action

export interface FeedbackAutomationActionModel {
    Id: string;
    Type: number;
    TagIds: string[];
    CategoryId?: string;
    IdeaId?: string;
}

export interface FeedbackAutomationAction {
    id: string;
    type: FeedbackAutomationActionType;
    tagIds?: string[];
    categoryId?: string;
    ideaId?: string;
}

export enum FeedbackAutomationActionType {
    AddTags = 10,
    MoveToCategory = 20,
    LinkToAnIdea = 30,
}
